/**
 * @ngdoc service
 * @name $branding
 * @description Generates the CSS variables for the auction branding
 **/

import { DOCUMENT } from '@angular/common'
import { Inject, Injectable } from '@angular/core'
import { AuctionEntity } from 'libs/gb-front/auction/data-access/src'
import { Theme } from './theme/theme.model'
import * as Color from 'color'
import { GbAuctionFooterVisibilityService } from 'libs/gb-front/auction/util/src/lib/gb-auction-footer-visibility/gb-auction-footer-visibility.service'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { GbColorService } from 'libs/shared/utils/services/src/lib/gb-color-service/gb-color.service'

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class BrandingService {
  styleElement!: HTMLElement

  // default colour pallet (below), this will be overriden by the colours set in the CMS

  public darkText = this.gbColorService.darkText
  public lightText = this.gbColorService.lightText

  public gbFont = 'Roboto, sans-serif'
  public gbFontSrc = 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500'

  public defaultTheme: Theme = {
    primary: '#39a080',
    accent: '#cb769e',
    background: '#f0f7f4',
    warning: '#f44336',
    success: '#e09891',
    bannerBackground: '#3c493f',
  }

  public themeObject: Theme = this.defaultTheme

  public themeContrast: Theme = {
    primary: this.lightText,
    accent: this.darkText,
    background: this.darkText,
    warning: this.lightText,
    success: this.darkText,
    bannerBackground: this.lightText,
  }

  public theme!: 'light' | 'dark'

  private shadeLevels: number[] = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900]

  constructor(
    @Inject(DOCUMENT) public document: Document,
    private gbAuctionFooterVisibilityService: GbAuctionFooterVisibilityService,
    private gbColorService: GbColorService,
  ) {}

  /**
   * initialises the theme
   * @param auction? auction object to take colours + custom css from, if undefined, use the default theme object
   **/

  initTheme(auction?: AuctionEntity): void {
    if (auction) {
      this.setThemeObject(auction)
      this.setCustomCss(auction.custom_css)
    }
    this.generateThemeCSS()
    this.setLightDark()
    this.setLightDarkPrimary()
    this.setGbTheme()
    this.setFooterClass()
  }

  /**
   * sets the theme object
   * @param auction auction object to take colours from
   **/

  setThemeObject(auction: AuctionEntity): void {
    this.themeObject.primary = auction.primary_color
    this.themeContrast.primary = this.getContrast(auction.primary_color)
    this.themeObject.accent = auction.accent_color
    this.themeContrast.accent = this.getContrast(auction.accent_color)
    this.themeObject.background = auction.background_color
    this.themeContrast.background = this.getContrast(auction.background_color)
    this.themeObject.warning = auction.warn_color
    this.themeContrast.warning = this.getContrast(auction.warn_color)
    this.themeObject.success = auction.success_color
    this.themeContrast.success = this.getContrast(auction.success_color)
    this.themeObject.bannerBackground = auction.banner_background_color
    this.themeContrast.bannerBackground = this.getContrast(auction.banner_background_color)
  }

  /**
   * Sets the auction theme back to the GalaBid default colors
   */

  clearTheme() {
    this.themeObject = this.defaultTheme
    this.generateThemeCSS()
  }

  /**
   * set the light / dark theme for the auction from the themeObject
   **/

  setLightDark(): void {
    const gbBackgroundColor = this.themeObject.background
    const color = Color(gbBackgroundColor)
    color.isLight() ? this.setLightTheme() : this.setDarkTheme()
  }

  setLightTheme(): void {
    this.theme = 'light'
    this.document.body.classList.remove('gb-dark-theme')
    this.document.body.classList.add('gb-light-theme')
  }

  setDarkTheme(): void {
    this.theme = 'dark'
    this.document.body.classList.remove('gb-light-theme')
    this.document.body.classList.add('gb-dark-theme')
  }

  setLightDarkPrimary(): void {
    const gbPrimaryColor = this.themeObject.primary
    const color = Color(gbPrimaryColor)
    color.isLight() ? this.setLightPrimary() : this.setDarkPrimary()
  }

  setGbTheme(): void {
    this.document.body.classList.add('gb-theme')
  }

  setFooterClass(): void {
    this.gbAuctionFooterVisibilityService.footerVisibility$.pipe(untilDestroyed(this)).subscribe((isVisible) => {
      isVisible
        ? this.document.body.classList.add('gb-footer-visible')
        : this.document.body.classList.remove('gb-footer-visible')
    })
  }

  setLightPrimary(): void {
    this.document.body.classList.remove('gb-dark-primary-theme')
    this.document.body.classList.add('gb-light-primary-theme')
  }

  setDarkPrimary(): void {
    this.document.body.classList.remove('gb-light-primary-theme')
    this.document.body.classList.add('gb-dark-primary-theme')
  }

  /**
   * generates the CSS variables for the auction using the colours saved in themeObject
   **/

  generateThemeCSS(): void {
    let key: keyof typeof this.themeObject
    let gbStyle = ''
    for (key in this.themeObject) {
      for (const shade of this.shadeLevels) {
        const col = this.themeObject[key]
        if (col && key != 'background') {
          gbStyle += `--gb-${key}-color-${shade}: ${this.getTintedColor(col, shade)};`
          gbStyle += `--gb-${key}-color-${shade}-contrast: ${this.getContrast(this.getTintedColor(col, shade))};`
        }
        if (col && key == 'background') {
          gbStyle += `--gb-${key}-color-${shade}: ${this.getTintedColor(col, shade, 0.001)};`
          gbStyle += `--gb-${key}-color-${shade}-contrast: ${this.getContrast(this.getTintedColor(col, shade, 0.001))};`
        }
      }
    }
    // document.documentElement.style.setProperty is not available on SSR
    document.documentElement.style.cssText = gbStyle
  }

  /**
   * get the tinted color as a hex string
   * @param inputColor color to modify
   * @param shade shade to convert the colour to (50 - 900)
   **/

  getTintedColor(inputColor: string, shade: number, tintStrength = 0.002): string {
    const color = Color(inputColor)

    const lightenColor = Color(this.lightText)
    const darkenColor = Color(this.darkText)

    let tintedColor: Color<string>

    const convertedShade: number = 500 - shade
    const tintAmount: number = convertedShade * tintStrength

    if (tintAmount >= 0) {
      tintedColor = color.mix(darkenColor, Math.abs(tintAmount))
    } else {
      tintedColor = color.mix(lightenColor, Math.abs(tintAmount))
    }

    return tintedColor.hex()
  }

  /**
   * Gets the contast to a given colour
   * @param inputColor colour to get the contast to
   **/

  getContrast(inputColor: string): string {
    return this.gbColorService.getContrast(inputColor)
  }

  /**
   * sets Custom Css for the auction
   * @param css string of the custom CSS for the auction
   **/

  private setCustomCss(css: string | undefined): void {
    if (!css) return

    if (!this.styleElement) {
      this.styleElement = this.createStyleElement()
    }

    this.styleElement.innerHTML = css
  }

  /**
   * creates a HTML Style element which is appended to the head of the document
   **/

  private createStyleElement(): HTMLStyleElement {
    const head = document.getElementsByTagName('head')[0]
    const style = document.createElement('style')
    style.className = 'gb-custom-css'
    head.appendChild(style)
    return style
  }
}
